"use client";
import { Form } from "@/app/components/shadcn/form";
import { Button } from "@/app/components/shadcn/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState, useEffect } from "react";
import {
  RegisterFormData,
  registerSchema,
  VerificationCodeData,
  verificationCodeSchema,
} from "@/app/schemas/registerSchema";
import useAuth from "@/app/lib/features/auth/auth.hook";
import { trackSignUp } from "@/app/lib/tracking/events";
import { syncConsentFromServer } from "@/app/lib/tracking/consent";
import { trackGeneric } from "@/app/lib/tracking/interactions";
import { useRouter } from "next/navigation";
import { AgreementsGroup } from "@/app/components/common/agreements";
import useAgreements from "@/app/lib/features/agreements/agreements.hook";
import RegisterInfoForm from "./register/RegisterInfoForm";
import VerificationForm from "./shared/VerificationForm";
import CountdownError from "./shared/CountdownError";
import { toast } from "sonner";
import { useAppDispatch } from "@/app/lib/features/redux/redux.hook";
import { clearHomeData } from "@/app/lib/features/home/home.slice";
import { clearError } from "@/app/lib/features/auth/auth.slice";

const RegisterForm = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { agreements } = useAgreements();
  const [isVerifying, setIsVerifying] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [agreementErrors, setAgreementErrors] = useState<Record<number, string>>({});

  const {
    sendRegistrationCode,
    verifyRegistrationCode,
    isLoading,
    authError,
  } = useAuth();

  // Unmount (sayfa değişimi) ve step değişiminde hata temizle
  useEffect(() => {
    dispatch(clearError());
  }, [showVerification, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      gender: "male",
      countryId: 212,
    },
    mode: "onChange",
  });

  const verificationForm = useForm<VerificationCodeData>({
    resolver: zodResolver(verificationCodeSchema),
    defaultValues: {
      code: "",
    },
  });

  const onSubmit = useCallback(
    async (data: RegisterFormData) => {
      const newAgreementErrors: Record<number, string> = {};
      agreements.filter((a) => a.isRequired).forEach((a) => {
        const val = (form.getValues as (name: string) => unknown)(`agreement_${a.id}`);
        if (!val) {
          newAgreementErrors[a.id] = "Bu sözleşmeyi kabul etmeniz zorunludur";
        }
      });

      if (Object.keys(newAgreementErrors).length > 0) {
        setAgreementErrors(newAgreementErrors);
        return;
      }

      setAgreementErrors({});

      const agreementValues = agreements.map((agreement) => ({
        id: agreement.id,
        value: data[`agreement_${agreement.id}`] ?? false,
      }));

      const registerData = {
        country_id: data.countryId,
        firstname: data.firstName,
        lastname: data.lastName,
        phone_number: data.phone,
        email: data.email,
        gender: data.gender,
        agreement: agreementValues,
        type: "b2c" as const,
      };

      const success = await sendRegistrationCode(registerData);

      if (success) {
        toast.success("Doğrulama kodu telefonunuza gönderildi.");
        setShowVerification(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.error(authError || "Kod gönderilemedi. Lütfen tekrar deneyin.");
      }
    },
    [sendRegistrationCode, agreements, authError, form],
  );

  const onVerificationSubmit = useCallback(
    async (data: VerificationCodeData) => {
      if (isVerifying) return;

      const formData = form.getValues();
      const agreementValues = agreements.map((agreement) => ({
        id: agreement.id,
        value: formData[`agreement_${agreement.id}`] ?? false,
      }));

      const registerData = {
        country_id: formData.countryId,
        firstname: formData.firstName,
        lastname: formData.lastName,
        phone_number: formData.phone,
        email: formData.email,
        gender: formData.gender,
        code: data.code,
        agreement: agreementValues,
        type: "b2c" as const,
      };

      setIsVerifying(true);

      const success = await verifyRegistrationCode(registerData);

      if (success) {
        trackSignUp("phone");
        trackGeneric("register");
        syncConsentFromServer();
        toast.success("Kayıt başarılı! Hoş geldiniz.");
        dispatch(clearHomeData());
        router.push("/");
      } else {
        toast.error(authError || "Geçersiz kod. Lütfen tekrar deneyin.");
        setIsVerifying(false);
      }
    },
    [form, verifyRegistrationCode, agreements, isVerifying, authError, dispatch, router],
  );

  const handleBackToRegister = useCallback(() => {
    setShowVerification(false);
    setIsVerifying(false);
    verificationForm.reset();
  }, [verificationForm]);

  const handleResendCode = useCallback(async () => {
    const formData = form.getValues();
    await onSubmit(formData);
  }, [form, onSubmit]);

  if (showVerification) {
    const phoneNumber = form.getValues("phone");
    return (
      <Form {...verificationForm}>
        <form onSubmit={verificationForm.handleSubmit(onVerificationSubmit)}>
          <VerificationForm
            form={verificationForm}
            phoneNumber={phoneNumber}
            submitButtonText="Hesap Oluştur"
            isLoading={isVerifying}
            error={authError}
            onBack={handleBackToRegister}
            onResend={handleResendCode}
            onErrorClear={() => dispatch(clearError())}
          />
        </form>
      </Form>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <RegisterInfoForm form={form} disabled={isLoading} />
        <AgreementsGroup
          form={form}
          fieldNamePrefix="agreement"
          disabled={isLoading}
          showTitle={true}
          title="İzinler ve Onaylar"
          agreementErrors={agreementErrors}
          onAgreementChange={(id) =>
            setAgreementErrors((prev) => {
              const next = { ...prev };
              delete next[id];
              return next;
            })
          }
        />
        <CountdownError
          error={authError}
          onClear={() => dispatch(clearError())}
        />
        <Button
          type="submit"
          className="w-full h-12 text-base font-medium"
          disabled={isLoading}
          onClick={() => {
            const errors: Record<number, string> = {};
            agreements.filter((a) => a.isRequired).forEach((a) => {
              const val = (form.getValues as (name: string) => unknown)(`agreement_${a.id}`);
              if (!val) {
                errors[a.id] = "Bu sözleşmeyi kabul etmeniz zorunludur";
              }
            });
            setAgreementErrors(errors);
          }}
        >
          {isLoading ? "Kod gönderiliyor..." : "Devam Et"}
        </Button>
      </form>
    </Form>
  );
};

export default RegisterForm;